/*!
 * (C) Ionic http://ionicframework.com - MIT License
 */

import {
    AfterViewInit,
    Component,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { BazisMediaQueryService } from '@bazis/shared/services/media-query.service';

const SUPPORTS_VARS = !!(window.CSS && window.CSS.supports && window.CSS.supports('--a: 0'));
const BREAKPOINTS = ['', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
const isRTL = document.dir === 'rtl';

@Component({
    selector: 'bazis-col',
    template: '<slot></slot>',
    styleUrls: ['col.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class ColElement implements AfterViewInit, OnChanges {
    @HostListener('window:resize') onResize() {
        this.setColStyles();
    }

    @Input() offset: string | undefined;

    @Input() offsetXs: string | undefined;

    @Input() offsetSm: string | undefined;

    @Input() offsetMd: string | undefined;

    @Input() offsetLg: string | undefined;

    @Input() offsetXl: string | undefined;

    @Input() offsetXxl: string | undefined;

    @Input() pull: string | undefined;

    @Input() pullXs: string | undefined;

    @Input() pullSm: string | undefined;

    @Input() pullMd: string | undefined;

    @Input() pullLg: string | undefined;

    @Input() pullXl: string | undefined;

    @Input() pullXxl: string | undefined;

    @Input() push: string | undefined;

    @Input() pushXs: string | undefined;

    @Input() pushSm: string | undefined;

    @Input() pushMd: string | undefined;

    @Input() pushLg: string | undefined;

    @Input() pushXl: string | undefined;

    @Input() pushXxl: string | undefined;

    @Input() size: string | undefined;

    @Input() sizeXs: string | undefined;

    @Input() sizeSm: string | undefined;

    @Input() sizeMd: string | undefined;

    @Input() sizeLg: string | undefined;

    @Input() sizeXl: string | undefined;

    @Input() sizeXxl: string | undefined;

    private _style;

    @HostBinding('style') get getColStyle() {
        if (!this._style) this.setColStyles();
        return this._style;
    }

    setColStyles() {
        this._style = {
            ...this.calculateSize(),
            ...this.calculatePush(isRTL),
            ...this.calculatePull(isRTL),
            ...this.calculateOffset(isRTL),
        };
    }

    constructor(private mqSrv: BazisMediaQueryService) {}

    ngOnChanges(changes: SimpleChanges): void {
        this.setColStyles();
    }

    ngAfterViewInit(): void {
        this.setColStyles();
    }

    private getColumns(property) {
        let matched;
        for (const breakpoint of BREAKPOINTS) {
            const matches = this.mqSrv.matchBreakpoint(breakpoint);
            // Grab the value of the property, if it exists and our
            // media query matches we return the value
            const columns =
                this[property + breakpoint.charAt(0).toUpperCase() + breakpoint.slice(1)];
            if (matches && columns !== undefined) {
                matched = columns;
            }
        }
        // Return the last matched columns since the breakpoints
        // increase in size and we want to return the largest match
        return matched;
    }

    private calculateSize() {
        const columns = this.getColumns('size');
        // If size wasn't set for any breakpoint
        // or if the user set the size without a value
        // it means we need to stick with the default and return
        // e.g. <nc-col size-md>
        if (!columns || columns === '') {
            return;
        }
        // If the size is set to auto then don't calculate a size
        const colSize =
            columns === 'auto'
                ? 'auto'
                : // If CSS supports variables we should use the grid columns var
                SUPPORTS_VARS
                ? `calc(calc(${columns} / var(--bazis-grid-columns, 12)) * 100%)`
                : // Convert the columns to a percentage by dividing by the total number
                  // of columns (12) and then multiplying by 100
                  (columns / 12) * 100 + '%';
        return {
            flex: `0 0 ${colSize}`,
            width: `${colSize}`,
            'max-width': `${colSize}`,
        };
    }

    // Called by push, pull, and offset since they use the same calculations
    private calculatePosition(property, modifier) {
        const columns = this.getColumns(property);
        if (!columns) {
            return;
        }
        // If the number of columns passed are greater than 0 and less than
        // 12 we can position the column, else default to auto
        const amount = SUPPORTS_VARS
            ? // If CSS supports variables we should use the grid columns var
              `calc(calc(${columns} / var(--bazis-grid-columns, 12)) * 100%)`
            : // Convert the columns to a percentage by dividing by the total number
            // of columns (12) and then multiplying by 100
            columns > 0 && columns < 12
            ? (columns / 12) * 100 + '%'
            : 'auto';
        return {
            [modifier]: amount,
        };
    }

    private calculateOffset(isRTL) {
        return this.calculatePosition('offset', isRTL ? 'margin-right' : 'margin-left');
    }

    private calculatePull(isRTL) {
        return this.calculatePosition('pull', isRTL ? 'left' : 'right');
    }

    private calculatePush(isRTL) {
        return this.calculatePosition('push', isRTL ? 'right' : 'left');
    }
}
